import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Textarea } from '../../../../components/Textarea';
import { Button } from '../../../../components/Button';
import { Switch, SwitchField, SwitchGroup } from '../../../../components/Switch';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import { AndroidRelease, AppReleaseService } from '../../../../services/appReleaseService';

const EditAppUpdatePage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [release, setRelease] = useState<AndroidRelease | null>(null);
    const [versionName, setVersionName] = useState('');
    const [versionCode, setVersionCode] = useState('');
    const [releaseNotes, setReleaseNotes] = useState('');
    const [setActive, setSetActive] = useState(false);

    useEffect(() => {
        document.title = 'Edit App Update';
    }, []);

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            try {
                setLoading(true);
                const data = await AppReleaseService.getAndroidReleaseById(Number(id));
                setRelease(data);
                setVersionName(data.version_name);
                setVersionCode(String(data.version_code));
                setReleaseNotes(data.release_notes || '');
                setSetActive(data.is_active === 1);
            } catch (error: any) {
                showErrorToast(error?.response?.data?.message || 'Failed to fetch app update');
            } finally {
                setLoading(false);
            }
        };
        void load();
    }, [id]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!release) return;

        if (!versionName.trim()) {
            showErrorToast('Version name is required');
            return;
        }
        if (!versionCode.trim() || Number.isNaN(Number(versionCode)) || Number(versionCode) <= 0) {
            showErrorToast('Version code must be a positive number');
            return;
        }

        try {
            setLoading(true);
            showLoadingToast('Updating app release...', 300);

            const updateRes = await AppReleaseService.updateAndroidReleaseMeta(release.id, {
                version_name: versionName.trim(),
                version_code: Number(versionCode),
                release_notes: releaseNotes.trim(),
            });

            if (!updateRes?.dbsuccess) {
                showErrorToast(updateRes?.message || 'Failed to update release');
                return;
            }

            if (setActive) {
                const activateRes = await AppReleaseService.activateAndroidRelease(release.id);
                if (!activateRes?.dbsuccess) {
                    showErrorToast(activateRes?.message || 'Failed to activate release');
                    return;
                }
            }

            showSuccessToast('App release updated successfully');
            navigate('/gp/app/updates');
        } catch (error: any) {
            showErrorToast(error?.response?.data?.message || 'Failed to update app release');
        } finally {
            setLoading(false);
        }
    };

    if (!release && !loading) return <p className="py-16 text-center">App release not found.</p>;

    return (
        <form onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Edit App Update</Heading>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className="ml-3" disabled={loading}>
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Release Details</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Update metadata and set this version as active.
                        </p>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Version Name</Subheading>
                            <div className="mt-2">
                                <Input value={versionName} onChange={(e) => setVersionName(e.target.value)} />
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Version Code</Subheading>
                            <div className="mt-2">
                                <Input type="number" value={versionCode} onChange={(e) => setVersionCode(e.target.value)} />
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Release Notes</Subheading>
                            <div className="mt-2">
                                <Textarea value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)} />
                            </div>
                        </div>
                        <div className="sm:col-span-full">
                            <SwitchGroup>
                                <SwitchField>
                                    <Switch
                                        checked={setActive}
                                        onChange={setSetActive}
                                        name="setActive"
                                    />
                                    <Subheading>Set as active release</Subheading>
                                </SwitchField>
                            </SwitchGroup>
                        </div>
                    </div>
                </div>
            </Card>
        </form>
    );
};

export default EditAppUpdatePage;
